import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { useEffect } from "react";
import type { Route } from "./+types/root";
import { AuthProvider } from "~/components/auth/AuthContext";
import { GlobalProvider } from "~/components/GlobalContext";
import PageNotFound from "~/components/PageNotFound";
import ErrorPage from "~/components/ErrorPage";
import TopBar from "~/components/Topbar";
import { type PostHog } from "posthog-js";
import { Text } from "~/components/ui/text";
import * as Sentry from "@sentry/react";
import { initPosthog } from "~/analytics";

import "./app.css";

declare global {
  interface Window {
    mina: { posthog: PostHog | null };
  }
}
export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <title>Mina</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {import.meta.env.PROD && (
          <link rel="icon" type="image/x-icon" href="/static/favicon.ico" />
        )}
      </head>
      <body className="h-full">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return (
    <div className="flex justify-center min-h-screen pt-[25vh]">
      <svg
        className="animate-spin h-6 w-6 text-gray-800"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
      <Text className="ml-4 text-lg font-semibold text-gray-800">
        Loading, please wait...
      </Text>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    // Initialize window.mina early
    if (!window.mina) {
      window.mina = { posthog: null };
    }
    
    // Initialize analytics asynchronously to not block hydration
    if (
      import.meta.env.PROD ||
      String(import.meta.env.VITE_FORCE_INSTRUMENTATION).toLowerCase() === "true"
    ) {
      // Use setTimeout with 0ms to defer until after current execution context
      setTimeout(() => {
        try {
          Sentry.init({
            dsn: "https://ab224883c4ccc346a62212fe280b7c8c@o1218784.ingest.us.sentry.io/4509718154641408",
            // Preserve collection limits across Sentry's new defaults.
            dataCollection: {
              userInfo: true,
              cookies: false,
              httpBodies: [],
              genAI: { inputs: false, outputs: false },
              databaseQueryData: false,
              queues: false,
              graphQL: { document: false, variables: false },
            },
            environment: import.meta.env.PROD ? "production" : "development",
            beforeSend(event) {
              // Avoid sending events that we handle ourselves
              if (event.tags && event.tags.handled === "yes") {
                return null;
              }
              return event;
            },
          });
          window.mina.posthog = initPosthog();
        } catch (error) {
          console.error("Failed to initialize analytics:", error);
          // App continues to work without analytics
        }
      }, 0);
    }
  }, []);
  
  return (
    <AuthProvider>
      <GlobalProvider>
        <TopBar />
        <Outlet />
      </GlobalProvider>
    </AuthProvider>
  );
}

function ErrorFallback({ error }: { error: unknown }) {
  return <div>We're sorry, but something went wrong: {String(error)}</div>;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Error encountered";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <PageNotFound />;
    }
    message = "Error";
    details = error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }
  Sentry.captureException(error, {
    extra: {
      message,
      details,
      stack,
    },
  });
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback error={error} />}>
      <ErrorPage message={message} details={details} stack={stack} />
    </Sentry.ErrorBoundary>
  );
}
